/* © 2024-2026 Johannes Roob. Proprietärer Code. Keine Nutzung oder Weitergabe ohne Erlaubnis. | Proprietary code. No use or distribution without permission. */

import { mustMap } from "../../util/must.js";
import { Taskbar } from "./Taskbar.js";
import { Window } from "./Window.js";

export class WindowManager {
    #stack = new WindowStack();
    #map: Map<string, Window> = new Map(); // Key: Window.Form.id
    #tray: Set<Window> = new Set();
    taskbar: Taskbar | undefined;

    add(w: Window) {
        // Set parent/child references.
        if (w.isModal && w.callingFormId !== "") {
            const parentWindow = mustMap(this.#map, w.callingFormId);
            parentWindow.modalChildWindow = w;
            parentWindow.dataset.isModalParent = true;
            w.modalParentWindow = parentWindow;
        }

        this.#map.set(w.id, w);

        if (this.taskbar !== undefined) {
            // Only create/delete Taskbar item for non-modal Window.
            if (w.modalParentWindow === undefined) {
                this.taskbar.create(w);
            }
        }

        this.activate(w);
    }

    delete(w: Window) {
        // Unlink this Window from its parent Window.
        if (w.modalParentWindow !== undefined) {
            w.modalParentWindow.modalChildWindow = undefined;
            w.modalParentWindow.dataset.isModalParent = false;
        }

        this.#stack.delete(w);
        this.#tray.delete(w);

        if (this.taskbar !== undefined) {
            // Only create/delete Taskbar item for non-modal Window.
            if (w.modalParentWindow === undefined) {
                this.taskbar.delete(w);
            }
        }

        this.activatePeek();
    }

    activate(w: Window) {
        const wPeek = this.#stack.peek();

        // Unactivate peek Window.
        if (wPeek !== undefined) {
            wPeek.unactivate();
        }

        // Need to delete complete chain.
        const wFirst = w.getOriginWindow();
        let wIter: Window = wFirst;

        do {
            // Delete and push Window to stack.
            this.#stack.delete(wIter);
            const index = this.#stack.push(wIter);
            wIter.setIndex(index);

            if (wIter.modalChildWindow === undefined) {
                break;
            }

            wIter = wIter.modalChildWindow;
        } while (true);

        // Activate last Window in chain.
        wIter.activate();

        if (wIter !== w) {
            wIter.playAnimationFlash();
        }

        if (this.taskbar !== undefined) {
            this.taskbar.activate(wFirst);
        }
    }

    minimize(w: Window) {
        if (w.getTerminalWindow() !== w) {
            return;
        }

        this.#stack.delete(w);
        this.#tray.add(w);

        if (this.taskbar !== undefined) {
            this.taskbar.unactivate(w);
        }

        w.playAnimationMinimize();
    }

    unminimize(w: Window) {
        if (w.getTerminalWindow() !== w) {
            return;
        }

        this.#tray.delete(w);
        this.activate(w);
        w.playAnimationUnminimize();
    }

    toggle(w: Window) {
        if (this.#tray.has(w)) {
            this.unminimize(w);
            return;
        }

        if (w === this.#stack.peek()) {
            this.minimize(w);
            return;
        }

        this.activate(w);
    }

    getWindowById(formId: string) {
        return this.#map.get(formId);
    }

    mustWindowById(formId: string) {
        return mustMap(this.#map, formId);
    }

    getActiveWindow() {
        return this.#stack.peek();
    }

    activatePeek() {
        const w = this.#stack.peek();

        if (w === undefined) {
            return;
        }

        this.activate(w);
    }

    destroyAll() {
        for (const w of this.#tray.values()) {
            w.destroy();
        }

        if (this.taskbar !== undefined) {
            this.taskbar.destroyAll();
        }
    }
}

class WindowStack {
    #indexStack = new NumberStack();
    #windows: Map<Window, number> = new Map();
    #windowsByIndex: Map<number, Window> = new Map();

    push(w: Window) {
        const newIndex = this.#indexStack.peek() + 1;
        this.#windows.set(w, newIndex);
        this.#windowsByIndex.set(newIndex, w);
        this.#indexStack.push(newIndex);
        return newIndex;
    }

    delete(w: Window) {
        const index = this.#windows.get(w);

        if (index === undefined) {
            return;
        }

        if (this.#indexStack.peek() === index) {
            this.#indexStack.pop();
        }

        this.#windows.delete(w);
        this.#windowsByIndex.delete(index);
    }

    index(w: Window) {
        return this.#windows.get(w);
    }

    peek() {
        const index = this.#indexStack.peek();
        return this.#windowsByIndex.get(index);
    }

    clear() {
        this.#indexStack.clear();
        this.#windows.clear();
        this.#windowsByIndex.clear();
    }
}

class NumberStack {
    #items: number[] = [];

    push(item: number) {
        this.#items.push(item);
    }

    pop() {
        return this.#items.pop();
    }

    peek() {
        if (this.isEmpty()) {
            return -1;
        }

        return this.#items[this.#items.length - 1];
    }

    isEmpty() {
        return this.#items.length === 0;
    }

    clear() {
        this.#items = [];
    }
}